import * as React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useFormContext } from './Form';
import { useFieldError } from './utils';

export interface TextFieldProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'name' | 'id'
> {
  /** RHF field name. Supports nested paths like `addresses.0.city`. */
  name: string;
  label: React.ReactNode;
  /**
   * Rendered on the input's own row, to its right — a mode switch, a unit
   * toggle, a "reset" link. The input takes the remaining width.
   *
   * Sits beside the `<input>` rather than inside the `<label>`: a button nested
   * in a label steals its own click into the input and lands in the input's
   * accessible name.
   */
  inputAccessory?: React.ReactNode;
  description?: React.ReactNode;
  containerClassName?: string;
  labelClassName?: string;
  /** Override the auto-generated id (optional). */
  id?: string;
  transformValue?: (value: string) => string;
}

export function TextField({
  name,
  label,
  inputAccessory,
  description,
  containerClassName,
  labelClassName,
  id,
  transformValue,
  ...inputProps
}: TextFieldProps) {
  const { register } = useFormContext();
  const error = useFieldError(name);
  const fieldId = id ?? `f-${name.replace(/\W+/g, '-')}`;
  const errorId = `${fieldId}-error`;
  const descId = description ? `${fieldId}-desc` : undefined;
  const registration = register(name);
  const { onChange, onBlur, ...restInputProps } = inputProps;
  const input = (
    <Input
      id={fieldId}
      aria-invalid={error ? true : undefined}
      aria-describedby={
        [error ? errorId : undefined, descId].filter(Boolean).join(' ') || undefined
      }
      {...registration}
      {...restInputProps}
      onBlur={(event) => {
        void registration.onBlur(event);
        onBlur?.(event);
      }}
      onChange={(event) => {
        if (transformValue) {
          const nextValue = transformValue(event.currentTarget.value);
          if (nextValue !== event.currentTarget.value) {
            event.currentTarget.value = nextValue;
          }
        }
        void registration.onChange(event);
        onChange?.(event);
      }}
    />
  );
  return (
    <div className={containerClassName}>
      <Label htmlFor={fieldId} className={labelClassName}>
        {label}
      </Label>
      {inputAccessory ? (
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">{input}</div>
          {inputAccessory}
        </div>
      ) : (
        input
      )}
      {description && (
        <p id={descId} className="mt-1 text-xs text-muted-foreground">
          {description}
        </p>
      )}
      {error && (
        <p id={errorId} className="mt-1 text-xs text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
